import { Suspense, useEffect } from 'react';
import { AppRoutes } from './router';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { AppErrorBoundary } from './components/base/ErrorBoundary';
import { ToastProvider } from './components/base/Toast';
import CookieConsentBanner from './components/feature/CookieConsentBanner';
import { CompareProvider } from './contexts/CompareContext';
import { CompareFloatingBar } from './components/feature/CompareFloatingBar';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
}

/** Tells the Service Worker the current path on every navigation */
function SwPathReporter() {
  const { pathname } = useLocation();
  useEffect(() => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'NAVIGATE',
        path: pathname,
      });
    }
  }, [pathname]);
  return null;
}

/**
 * Re-arms the LCP observer on every React Router navigation.
 * Lazy-imports the utility so it stays out of the critical bundle.
 */
function RouteChangeLCPObserver() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Small delay so the new page's paint has started before we arm the observer
    const id = setTimeout(() => {
      import('./utils/routePrefetch').then(({ resetLCPObserver }) => {
        resetLCPObserver();
      }).catch(() => {});
    }, 100);
    return () => clearTimeout(id);
  }, [pathname]);
  return null;
}

function RouteLoadingFallback() {
  return (
    <div className="min-h-screen bg-white">
      <div className="h-[72px] md:h-[120px] bg-white border-b border-gray-200" />
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3 animate-pulse" />
          <div className="h-4 bg-gray-200 rounded w-2/3 animate-pulse" />
          <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse" />
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <AppErrorBoundary>
      <ToastProvider>
        <BrowserRouter basename={__BASE_PATH__}>
          <ScrollToTop />
          <SwPathReporter />
          <RouteChangeLCPObserver />
          <Suspense fallback={<RouteLoadingFallback />}>
            <CompareProvider>
              <AppRoutes />
              <CompareFloatingBar />
            </CompareProvider>
          </Suspense>
          <CookieConsentBanner />
        </BrowserRouter>
      </ToastProvider>
    </AppErrorBoundary>
  );
}

export default App;
